import type { CheckoutSession, PaymentOption } from "@/types";
import { encodeCurrency } from "@/lib/codec";
import type { CksPaymentType } from "@/services/cosmos/schema/checkout-service";

import { getTracker, trackSessionEvent, buildPpwsPayload } from "./analytics-helpers";

export function emitPaymentPlanTracking(
  eventType: "default_payment_plan_was_shown" | "user_switched_payment_plan",
  paymentOption: PaymentOption,
  session: CheckoutSession,
): void {
  const tracker = getTracker();
  if (
    !tracker ||
    !session.merchant?.id ||
    !session.id ||
    !session.order?.id ||
    !session.customerId ||
    !session.order?.countryCode
  ) {
    return;
  }

  const currencyCode = encodeCurrency(session.order.currency) ?? "SAR";
  const ppwsPayload = buildPpwsPayload(paymentOption, session);

  tracker.track({
    eventType,
    merchantId: session.merchant.id,
    checkoutId: session.id,
    orderId: session.order.id,
    customerId: session.customerId,
    countryCode: session.order.countryCode,
    currencyCode,
    eventData: {
      offerId: paymentOption.offerId,
      instalments: paymentOption.tenure,
      ppws: ppwsPayload,
    },
  });
}

export function emitPifShown(
  paymentOption: PaymentOption,
  session: CheckoutSession,
): void {
  const tracker = getTracker();
  if (
    !tracker ||
    !session.merchant?.id ||
    !session.id ||
    !session.order?.id ||
    !session.customerId ||
    !session.order?.countryCode
  ) {
    return;
  }

  const currencyCode = encodeCurrency(session.order.currency) ?? "SAR";
  const hasOtherPaymentOptions = (session.paymentOptions?.length ?? 0) > 1;
  const ppwsPayload = buildPpwsPayload(
    paymentOption,
    session,
    "PAY_NOW",
    hasOtherPaymentOptions,
  );

  tracker.track({
    eventType: "pif_shown",
    merchantId: session.merchant.id,
    checkoutId: session.id,
    orderId: session.order.id,
    customerId: session.customerId,
    countryCode: session.order.countryCode,
    currencyCode,
    eventData: {
      offerId: paymentOption.offerId,
      instalments: paymentOption.tenure,
      ppws: ppwsPayload,
    },
  });
}

export function emitPnmShown(
  paymentOption: PaymentOption,
  session: CheckoutSession,
): void {
  const tracker = getTracker();
  if (!tracker) {
    return;
  }

  const currencyCode = encodeCurrency(session.order.currency) ?? "SAR";
  const hasOtherPaymentOptions = (session.paymentOptions?.length ?? 0) > 1;
  const ppwsPayload = buildPpwsPayload(
    paymentOption,
    session,
    "PAY_NEXT_MONTH",
    hasOtherPaymentOptions,
  );

  tracker.track({
    eventType: "pnm_shown",
    merchantId: session.merchant.id,
    checkoutId: session.id,
    orderId: session.order.id,
    customerId: session.customerId,
    countryCode: session.order.countryCode,
    currencyCode,
    eventData: {
      offerId: paymentOption.offerId,
      instalments: paymentOption.tenure,
      ppws: ppwsPayload,
    },
  });
}

export function emitNoPaymentPlanTracking(
  session: CheckoutSession,
  payInFullAvailable: boolean,
): void {
  trackSessionEvent("no_payment_plan_to_show", session, {
    PayInFullAvailable: payInFullAvailable,
  });
}

export function emitPaymentPlanSelectionTracking(
  eventType:
    | "continue_with_option_button_clicked"
    | "pay_in_full_button_clicked"
    | "pay_next_month_button_clicked"
    | "order_details_viewed",
  session: CheckoutSession,
  eventData?: {
    instalments?: number;
    payment_type?: CksPaymentType;
    offer_id?: string;
    visual_index?: number;
  },
): void {
  trackSessionEvent(eventType, session, eventData ?? {});
}

/**
 * Emit legacy plan-details bottom sheet tracking events that were missed
 * during the checkout → payment-plans-ui migration. The eventType strings
 * are preserved verbatim from the old checkout repo so the events land
 * unchanged in BigQuery `checkout_frontend_track_events`.
 */
export function emitPaymentDetailsBottomSheetTracking(
  eventType:
    | "payment__payment_details_clicked"
    | "payment__payment_details_close_btn_clicked"
    | "payment__details_viewed",
  paymentOption: PaymentOption,
  session: CheckoutSession,
  paymentType: CksPaymentType = "PAY_BY_INSTALMENTS",
): void {
  const tracker = getTracker();

  if (
    !tracker ||
    !session.merchant?.id ||
    !session.id ||
    !session.order?.id ||
    !session.customerId ||
    !session.order?.countryCode
  ) {
    return;
  }

  const currencyCode = encodeCurrency(session.order.currency) ?? "SAR";

  tracker.track({
    eventType,
    merchantId: session.merchant.id,
    checkoutId: session.id,
    orderId: session.order.id,
    customerId: session.customerId,
    countryCode: session.order.countryCode,
    currencyCode,
    eventData: {
      product_flow: "cosmos",
      payment_type: paymentType,
      murabaha_profit: paymentOption.termCostAmount.amount,
      processing_fee: paymentOption.processingFeeAmount.amount,
      total_processing_fee: paymentOption.totalCustomerFeeAmount.amount,
      apr: paymentOption.annualPercentageRate ?? 0,
      select_finance_instalments: paymentOption.tenure,
      offer_id: paymentOption.offerId,
    },
  });
}
